/**
 * tio-api plan eligibility conditions (mirrors Plan::ELIGIBILITY_CONDITIONS)
 * that indicate an SLR loan-standing verification requirement. A plan carrying
 * any of these requires the employee to periodically prove their loans are in
 * good standing.
 */
export const SLR_LOAN_STANDING_ELIGIBILITY_CONDITIONS = [
  'require_document',
  'require_recent_document',
  'require_audit_window_upload',
  'allow_late_requalification',
  'require_name_match',
  'require_required_observations',
  'require_good_standing_status',
  'reject_parent_plus_only',
] as const;

const KNOWN_CONDITIONS = new Set<string>(SLR_LOAN_STANDING_ELIGIBILITY_CONDITIONS);

/**
 * Whether a plan requires SLR loan-standing verification, derived from its
 * `eligibility_conditions`. This is the source of truth for the feature being
 * "enabled" for a plan — tio-api does not send a dedicated `featureEnabled`
 * flag. Wiring this into the dashboard alert gating (replacing the imagined
 * `state.featureEnabled`) is tracked in tio-front-end#8714.
 */
export function planRequiresLoanStandingVerification(
  eligibilityConditions?: readonly string[] | null
): boolean {
  if (!eligibilityConditions) return false;
  return eligibilityConditions.some((condition) => KNOWN_CONDITIONS.has(condition));
}

/**
 * The upload guidelines shown on the verification card, derived from the plan's
 * SELECTED eligibility conditions (tio-admin → plans/eligibility-conditions-editor)
 * — never a fixed list. Each condition that constrains what the DOCUMENT must
 * show contributes one numbered line; conditions that govern whether/when a
 * document is required (`require_document`, `require_audit_window_upload`,
 * `allow_late_requalification`) or that reject a loan type outright
 * (`reject_parent_plus_only`) add no line here.
 */
const CONDITION_GUIDELINES = [
  {
    condition: 'require_recent_document',
    labelKey: 'slr.loan_standing.attention.check_recent_label',
    textKey: 'slr.loan_standing.attention.check_recent',
  },
  {
    condition: 'require_name_match',
    labelKey: 'slr.loan_standing.attention.check_name_label',
    textKey: 'slr.loan_standing.attention.check_name',
  },
  {
    condition: 'require_required_observations',
    labelKey: 'slr.loan_standing.attention.check_details_label',
    textKey: 'slr.loan_standing.attention.check_details',
  },
  {
    condition: 'require_good_standing_status',
    labelKey: 'slr.loan_standing.attention.check_status_label',
    textKey: 'slr.loan_standing.attention.check_status',
  },
] as const;

export type LoanStandingGuideline = (typeof CONDITION_GUIDELINES)[number];

/**
 * Guidelines the plan's conditions actually call for. When conditions are
 * absent (payload not loaded / plan not carrying them) we fall back to the
 * three document-content guidelines that shipped before this was
 * condition-driven, so the card never renders an empty checklist.
 */
export function loanStandingGuidelines(
  eligibilityConditions?: readonly string[] | null
): readonly LoanStandingGuideline[] {
  if (!eligibilityConditions?.length) {
    return CONDITION_GUIDELINES.filter(
      (guideline) => guideline.condition !== 'require_good_standing_status'
    );
  }
  const selected = new Set(eligibilityConditions);
  return CONDITION_GUIDELINES.filter((guideline) => selected.has(guideline.condition));
}
